import { Phone, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Pillar = {
  number: string;
  category: string;
  title: string;
  desc: string;
  proof: string;
};
const PILLARS: Pillar[] = [{
  number: "01",
  category: "Specialisation",
  title: "Roller doors aren't a sideline.",
  desc: "Most garage guys do whatever walks in. We've focused on roller doors since 2009 — every weak spot, every brand quirk, every silent failure mode. It's the difference between a generalist and a craftsman.",
  proof: "4,200+ roller doors serviced"
}, {
  number: "02",
  category: "Speed",
  title: "Same day, not 'sometime this week.'",
  desc: "Your roller door is your security and your way to work. We stock common parts in the van so we fix on first visit — not return three days later with the right spring.",
  proof: "Avg. response · 22 minutes"
}, {
  number: "03",
  category: "Honesty",
  title: "Fixed quote before any spanner moves.",
  desc: "You see the price before we start. No 'while we're here' upsells, no last-minute surprises, no holding your door hostage. If you don't like the quote — we leave. Free callout.",
  proof: "Written quote · No deposit"
}, {
  number: "04",
  category: "Guarantee",
  title: "12-month workmanship warranty in writing.",
  desc: "Every job comes with a written warranty on the work and the parts. Something fails inside the warranty period — we come back, free of charge, full stop. We're a family business in Bass Hill, not a fly-by-night.",
  proof: "12mo workmanship + parts"
}];
const SOCIAL_PROOF = [{
  metric: "5.0★",
  label: "Google rated · 100+ reviews"
}, {
  metric: "2009",
  label: "Family-owned since"
}, {
  metric: "$0",
  label: "Callout fee"
}, {
  metric: "150+",
  label: "Sydney suburbs covered"
}];
export default function RollerWhyUs() {
  return <section style={{
    background: CREAM,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      {/* Decorative watermark */}
      <div aria-hidden style={{
      position: "absolute",
      top: 60,
      right: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 300px)",
      fontWeight: 900,
      color: CREAM_DEEP,
      opacity: 0.55,
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 0
    }}>
        WHY US
      </div>

      {/* Hairline gold rule */}
      <div aria-hidden style={{
      position: "absolute",
      top: 80,
      left: 24,
      width: 120,
      height: 1,
      background: GOLD,
      zIndex: 1
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 80,
        alignItems: "end"
      }} className="roller-why-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD_DARK
            }}>
                05 / Why Sydney chooses Gold
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: INK,
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              The reasons{" "}
              <span style={{
              color: GOLD_DARK,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                we get called back
              </span>
              <br />
              when other guys vanish.
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: INK_MUTED,
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            17 years of repairs taught us four things matter more than anything
            else. Everything we do is built around them.
          </p>
        </div>

        {/* === 4 PILLARS - LARGE EDITORIAL LIST === */}
        <div style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: 80
      }}>
          {PILLARS.map((p, i) => <PillarRow key={i} pillar={p} isLast={i === PILLARS.length - 1} />)}
        </div>

        {/* === NAVY STAT BAR === */}
        <div style={{
        position: "relative",
        background: NAVY,
        borderRadius: 4,
        padding: "48px 48px",
        overflow: "hidden"
      }}>
          {/* Gold corner */}
          <div aria-hidden style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 4,
          height: "100%",
          background: GOLD
        }} />

          {/* Soft radial glow */}
          <div aria-hidden style={{
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: 500,
          height: 500,
          background: `radial-gradient(circle, rgba(245,166,35,0.08) 0%, transparent 60%)`,
          pointerEvents: "none"
        }} />

          <div style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 48,
          alignItems: "center"
        }} className="roller-why-stats-grid">
            <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 32
          }} className="roller-why-stats">
              {SOCIAL_PROOF.map((s, i) => <div key={i} style={{
              borderLeft: i === 0 ? "none" : `1px solid rgba(255,255,255,0.1)`,
              paddingLeft: i === 0 ? 0 : 24
            }}>
                  <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "clamp(28px, 3vw, 40px)",
                fontWeight: 800,
                color: GOLD,
                letterSpacing: "-0.025em",
                lineHeight: 1,
                marginBottom: 10
              }}>
                    {s.metric}
                  </div>
                  <div style={{
                fontFamily: FONT_BODY,
                fontSize: 13,
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.4,
                fontWeight: 500
              }}>
                    {s.label}
                  </div>
                </div>)}
            </div>

            <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 10
          }}>
              <a href="tel:0485000933" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "16px 24px",
              background: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontSize: 15,
              fontWeight: 700,
              borderRadius: 8,
              textDecoration: "none",
              whiteSpace: "nowrap",
              transition: "transform 0.2s, box-shadow 0.2s",
              boxShadow: "0 6px 20px rgba(245,166,35,0.3)"
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.45)";
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.3)";
            }}>
                <Phone size={16} strokeWidth={2.5} />
                0485 000 933
              </a>
              <Link to="#contact-us" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "14px 24px",
              background: "transparent",
              color: "#ffffff",
              fontFamily: FONT_HEAD,
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              textDecoration: "none",
              border: "1px solid rgba(255,255,255,0.25)",
              whiteSpace: "nowrap",
              justifyContent: "center",
              transition: "background 0.2s, border-color 0.2s"
            }} onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.08)";
              (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.5)";
            }} onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
              (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.25)";
            }}>
                Free quote
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .roller-why-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .roller-why-stats-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .roller-why-stats {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 24px !important;
          }
          .roller-why-stats > div {
            border-left: none !important;
            padding-left: 0 !important;
          }
        }
      `}</style>
    </section>;
}

// === Pillar Row (large editorial format) ===
function PillarRow({
  pillar,
  isLast
}: {
  pillar: Pillar;
  isLast: boolean;
}) {
  return <div className="roller-pillar-row" style={{
    display: "grid",
    gridTemplateColumns: "120px minmax(0, 1.4fr) minmax(0, 1fr)",
    gap: 48,
    padding: "40px 0",
    borderBottom: isLast ? "none" : `1px solid ${CREAM_DEEP}`,
    alignItems: "start"
  }}>
      {/* Number marker */}
      <div>
        <div style={{
        fontFamily: FONT_HEAD,
        fontSize: 48,
        fontWeight: 800,
        color: GOLD,
        letterSpacing: "-0.02em",
        lineHeight: 1,
        marginBottom: 8
      }}>
          {pillar.number}
        </div>
        <div style={{
        fontFamily: FONT_BODY,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: GOLD_DARK
      }}>
          {pillar.category}
        </div>
      </div>

      {/* Title + description */}
      <div>
        <h3 style={{
        fontFamily: FONT_HEAD,
        fontSize: "clamp(22px, 2.2vw, 30px)",
        fontWeight: 700,
        color: INK,
        lineHeight: 1.2,
        letterSpacing: "-0.015em",
        margin: "0 0 14px"
      }}>
          {pillar.title}
        </h3>
        <p style={{
        fontFamily: FONT_BODY,
        fontSize: 16,
        color: INK_MUTED,
        lineHeight: 1.6,
        margin: 0
      }}>
          {pillar.desc}
        </p>
      </div>

      {/* Proof card */}
      <div style={{
      padding: "20px 24px",
      background: "#ffffff",
      border: `1px solid ${CREAM_DEEP}`,
      borderLeft: `3px solid ${GOLD}`,
      borderRadius: 4
    }}>
        <div style={{
        fontFamily: FONT_BODY,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: GOLD_DARK,
        marginBottom: 8
      }}>
          The proof
        </div>
        <div style={{
        fontFamily: FONT_HEAD,
        fontSize: 17,
        fontWeight: 700,
        color: INK,
        lineHeight: 1.3,
        letterSpacing: "-0.01em"
      }}>
          {pillar.proof}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .roller-pillar-row {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
            padding: 32px 0 !important;
          }
        }
      `}</style>
    </div>;
}